import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Col, Row, FormGroup, FormControl } from "react-bootstrap";
import ReactDatePicker from "react-datepicker";
import { MdAccountCircle, MdPlace } from "react-icons/md";
import { BsClockFill, BsFillCalendarEventFill, BsCalendarCheck } from "react-icons/bs";

import Lead from "../../shared/Lead";
import TextArea from "../../shared/TextArea";
import Patient from "../../shared/Patient";
import SelectAssignee from "./SelectAssignee";
import SelectPcp from "./SelectPcp";
import SelectVisitType from "./SelectVisitType";
import Select from "../../shared/Select";
import FieldError from "../../shared/FieldError";
import { Settings } from "../../helpers/Contexts";
import * as API from "../../api";
import * as CONST from "../../helpers/Constants";
const AppointmentCompletedForm = ({ dispatch, state, patient, isCall, formHandler }) => {
  const [error, setError] = useState(null);
  const [taskSubmitted, setTaskSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const settings = useContext(Settings);

  const CompletedAppointmentSchema = Yup.object().shape({
    appointment_source: Yup.string().required("Appointment source is required"),
    appointment_initiated_date: Yup.date().required("Date appointment was initiated is required"),
    appointment_time: Yup.string().matches(
      "([01]?[0-9]|2[0-3]):[0-5][0-9]",
      "Appointment time must be in a valid 12 hour (e.g HH:MM AM/PM) or military (e.g. 13:00) format. "
    ),
    document: Yup.mixed(),
    completed_on_date: Yup.date().required("Appointment date is required."),
    assignee: Yup.string().required(
      "Assignee is required. Assign this task to either a suppport group or agent."
    ),
    procedure_type: Yup.string().required("Visit type is required"),
    pcp: Yup.string().nullable(),
  });

  const CompletedAppointmentInitialValues = {
    appointment_source: "",
    appointment_initiated_date: "",
    order_date: "",
    notes: "",
    procedure_type_id: "",
    procedure_type: "",
    pcp: "",
    pcp_id: "",
    pcp_practice_name: "",
    pcp_practice_id: "",
    pcp_practice_address: "",
    assigner_id: settings.me.id,
    assignee_id: "",
    assignee_support_group_id: "",
    assignee: "",
    document: "",
    date: "",
    completed_on_date: "",
    appointment_date: "",
    appointment_time: "",
  };

  const handleSubmit = async (values) => {
    const formData = new FormData();
    values.document
      ? formData.append("task[document]", values.document) &&
        formData.append(
          "task[sub_status_list_id]",
          settings.all_sub_statuses.filter((i) => i.name == CONST.CPR.RESULTS_OBTAINED)[0].id
        )
      : formData.append(
          "task[sub_status_list_id]",
          settings.all_sub_statuses.filter((i) => i.name == CONST.CPR.RESULTS_PENDING)[0].id
        );
    formData.append("task[status]", "open");
    formData.append("task[task_type]", "appointment_request");
    formData.append("task[appointment_request_status]", "appointment_completed");
    formData.append("task[assigner_id]", settings.me.id);
    formData.append("task[assigner_support_group_id]", settings.me.support_group_id);
    formData.append("task[patient_id]", state.create.patient.id);
    formData.append("task[appointment_source]", values.appointment_source);
    formData.append("task[order_date]", values.order_date);
    formData.append("task[assignee_id]", values.assignee_id);
    formData.append("task[assignee_support_group_id]", values.assignee_support_group_id);
    formData.append("task[appointment_time]", values.appointment_time);
    formData.append("task[date_type]", "Appointment Date");
    formData.append("task[appointment_date]", values.appointment_date);
    formData.append("task[completed_on_date]", values.completed_on_date);
    formData.append("task[procedure_type_id]", values.procedure_type_id);
    formData.append("task[attending_provider]", values.pcp);
    formData.append("task[attending_provider_id]", values.pcp_id);
    formData.append("task[pcp_practice_id]", values.pcp_practice_id);
    formData.append("task[notes]", values.notes);

    if (isCall) {
      return formHandler(formData);
    } else {
      return API.postTask(formData)
        .then((res) => {
          setLoading(false);
          setTaskSubmitted(true);
          dispatch({ type: "set_loading", payload: true });
          history.push(`/physicians/tasks/item/${res.task.id}`);
        })
        .catch((err) => {
          alert(err.error);
          document.body.scrollTop = document.documentElement.scrollTop = 0;
          setError(err);
        });
    }
  };

  return (
    <>
      <Formik
        initialValues={CompletedAppointmentInitialValues}
        validationSchema={CompletedAppointmentSchema}
        validateOnChange={true}
        onSubmit={async (values) => {
          setLoading(true);
          const submitted = await handleSubmit(values);
        }}
      >
        {({ values, setFieldValue, errors, isSubmitting }) => {
          return (
            <Form>
              <Row className="flex">
                <Col md={5} className="form">
                  <Lead message={isCall ? "Appointment Completed" : "New Appointment Completed"} />
                  <br />
                  { error && (
                    <>
                      <div className="awv-pop-up red-text">
                        <b>{ error.error }</b>
                        { error.task_ids && (<>
                          <br />
                          <b>Please check these open tasks:</b>
                          { error.task_ids && error.task_ids.map((taskId) => {
                            return (<a target={'_blank'} className={'btn'} href={`/physicians/tasks/item/${ taskId }`}>
                              Task { taskId }
                            </a>)
                          })}
                          </>) }

                          { !error.task_ids && (<>
                            <br />
                            <a onClick={async (el) => {
                              let res = await API.postVisitStatusCheck({ cpr: {
                                id: error.cpr_id
                              }})

                              if (res) {
                                window.location.href = `/physicians/tasks/item/${ res.task_id }`;
                              };
                            }}>
                              Trigger Visit Status Check for CPR { error.cpr_id }
                            </a>
                            </>)}
                      </div>
                    </>
                  )}
                  <br />
                  <SelectAssignee name="assignee" setFieldValue={setFieldValue} />
                  <Row>
                    <Col md={6} id="field-left">
                      <label>{"Appointment Initiated Date"}</label>
                      <div className={"flex datefield-wrapper"}>
                        <Field name="appointment_initiated_date">
                          {({ field, meta }) => {
                            return (
                              <>
                                <BsFillCalendarEventFill className="calendar-icon" />
                                <ReactDatePicker
                                  autoComplete="off"
                                  {...field}
                                  dateFromat={"YYYY-MM-dd"}
                                  className="datepicker"
                                  onChange={(date) => {
                                    setFieldValue(
                                      "appointment_initiated_date",
                                      date.toISOString().slice(0, 10)
                                    );
                                    setFieldValue("order_date", date);
                                  }}
                                />
                              </>
                            );
                          }}
                        </Field>
                      </div>
                    </Col>
                    <Col md={6} id="field-right">
                      <label>Appointment Source</label>
                      <Select name="appointment_source">
                        <option value={""}>{"Select appointment source"}</option>
                        {settings.appointment_sources.map((option, i) => (
                          <option key={i} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </Col>
                  </Row>

                  <SelectVisitType
                    name="procedure_type"
                    setFieldValue={setFieldValue}
                    showPopup={true}
                  />
                  <label>{"Attending Provider"}</label>
                  {values.procedure_type == "Lab/Diagnostic Only" ? (
                    <select disabled className="dropdown-select">
                      <option value={""}>{"N/A for Lab/Diagnostic Only"}</option>
                    </select>
                  ) : (
                    <SelectPcp name="pcp" pcpRequired={true} active={false} required={true} setFieldValue={setFieldValue} />
                  )}

                  <label>Completed on Date & Time</label>
                  <div className="flex">
                    <div className={"flex datefield-wrapper"}>
                      <Field name="date">
                        {({ field, meta }) => {
                          return (
                            <>
                              <BsFillCalendarEventFill className="calendar-icon" />
                              <ReactDatePicker
                                autoComplete="off"
                                {...field}
                                className="datepicker"
                                dateFromat={"YYYY-MM-dd"}
                                maxDate={
                                  settings.me.environment == "production" ? new Date() : null
                                }
                                onChange={(date) => {
                                  setFieldValue("appointment_date", date);
                                  setFieldValue("completed_on_date", date);
                                  setFieldValue("date", date.toISOString().slice(0, 10));
                                }}
                              />
                            </>
                          );
                        }}
                      </Field>
                    </div>
                    <div className={"flex datefield-wrapper"}>
                      <BsClockFill className="calendar-icon" />
                      <Field name="appointment_time" className="datepicker" />
                    </div>
                  </div>
                  <div className="mt10">
                    <Col md={6} id="field-left">
                      {errors.appointment_date ? (
                        <FieldError error={errors.appointment_date} />
                      ) : null}
                    </Col>
                    <Col md={6} id="field-right">
                      {errors.appointment_time ? (
                        <FieldError error={errors.appointment_time} />
                      ) : null}
                    </Col>
                  </div>
                  <TextArea label="Notes" name="notes" placeholder="Write additional notes" />
                  <label>Results/Visit Summary</label>
                  <span id="from">{"Attach results/visit summary document if available"}</span>
                  <FormGroup controlId="formFile" className="mb-3">
                    <FormControl
                      type="file"
                      onChange={(e) => setFieldValue("document", e.target.files[0])}
                    />
                  </FormGroup>
                  <div className="form-button mt20-bt">
                    <button id="btn outline" type="submit" disabled={isSubmitting}>
                      {"Submit Appointment Completed"}
                    </button>
                  </div>
                </Col>

                <Col md={7} className={"pad20 border-container"}>
                  <Patient patient={{ ...state.create.patient }} />
                  <hr />
                  <label className="agent">
                    {"Assignee "}{" "}
                    <span id="from">
                      {values.assignee && (
                        <>
                          <MdAccountCircle className="task-agent" />
                          <b> {values.assignee}</b>
                        </>
                      )}
                    </span>
                  </label>
                  <hr />
                  {!isCall && (
                    <>
                      {" "}
                      <Lead message={state.create.type} />
                      <br />
                    </>
                  )}
                  <BsCalendarCheck id="appointment-big-icon" />
                  <label>{"Visit Type"}</label>
                  {values.procedure_type}
                  <label>{"Attending Provider"}</label>
                  {values.procedure_type == "Lab/Diagnostic Only"
                    ? "N/A for Lab/Diagnostic Only"
                    : values.pcp}
                  {values.pcp_practice_id && (
                    <>
                      <label>Location</label>
                      <MdPlace className="pcp-icon" />
                      {values.pcp_practice_name}
                      <br />
                      {values.pcp_practice_address}
                    </>
                  )}
                  <br />
                  <div className="mt10 mb10">
                    <label>Completed on Date & Time </label>
                    {values.date && (
                      <>
                        <BsFillCalendarEventFill className="cpr-label-icon" />
                        <b>{" " + values.date + " "}</b>
                      </>
                    )}
                    {values.appointment_time && (
                      <>
                        <BsClockFill className="cpr-label-icon" />
                        <b>{values.appointment_time} </b>
                        {"  "}(Pacific Standard/Daylight Time)
                      </>
                    )}
                  </div>
                  <label>Notes</label>
                  {values.notes}
                  <label>{"Appointment Initated Date"}</label>
                  {values.appointment_initiated_date}
                  <label>{"Appointment Source"}</label>
                  {values.appointment_source &&
                    values.appointment_source
                      .replaceAll("_", " ")
                      .replaceAll("-", " ")
                      .toUpperCase()}
                </Col>
              </Row>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default AppointmentCompletedForm;
